import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import ReactDOM from "react-dom";
import { useLocation, useHistory } from "react-router-dom";
import { get, isEmpty } from "lodash";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { countries } from "countries-list";

import ArrowBackIos from "@material-ui/icons/ArrowBackIos";
import Property from "../../../model/property";
import Guests from "../../../model/guests";
import Booking from "../../../model/booking";

import PropertiesService from "../../../backend/PropertiesService";
import GuestServicesService from "../../../backend/GuestServicesService";
import PresetsService from "../../../backend/PresetsService";
import ConfigurationsService from "../../../backend/ConfigurationsService";
import TokenService from "../../../backend/TokenService";
import BookingService from "../../../backend/BookingService";
import storeFunction from "../../../utils/storeFunction";

import BrandStyleSheet from "../BrandStyleSheet";
import LoadingPage from "../LoadingPage";
import ErrorPage from "../ErrorPage";
import ErrorModal from "../../../components/modal/ErrorModal";
import ButtonLanguage from "../../../components/ButtonLanguage";
import CopyRightAndPrivacyComponent from "../components/CopyRightAndPrivacyComponent";

import { Dummy } from "../Dummy";
import { HotelKeyDummy } from "../HotelKeyDummy";
import { UpcomingDummy } from "../UpcomingDummy";
import useGetBrandTranslation from "../../../i18n/locales/useGetBrandTranslation";

const PreCheckInContext = createContext();

export const usePreCheckInContext = () => {
  const context = useContext(PreCheckInContext);
  if (!context) {
    throw new Error(
      "usePreCheckInContext must be used within a PreCheckInContext.Provider"
    );
  }
  return context;
};

const MAX_RETRY_COUNT = 30;

export const PreCheckInContextProvider = ({ children }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const [isMounted, setIsMounted] = useState(true);
  const [isBookingMounted, setIsBookingMounted] = useState(true);
  const [isUpcomingStaysMounted, setIsUpcomingStaysMounted] = useState(true);
  const [keepLoading, setKeepLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorApi, setErrorApi] = useState(false);
  const [language, setLanguage] = useState();
  const [userEmail, setUserEmail] = useState(query.get("email"));
  const [arrivalDate, setArrivalDate] = useState(null);
  const [propertyUid, setPropertyUid] = useState(null);
  const [bookingNumber, setBookingNumber] = useState(null);
  const [lastName, setLastName] = useState(null);
  const [property, setProperty] = useState(null);
  const [guestService, setGuestService] = useState(null);
  const [presets, setPresets] = useState(null);
  const [configuration, setConfiguration] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [booking, setBooking] = useState(null);
  const [guests, setGuests] = useState(null);
  const [resetBooking, setResetBooking] = useState(false);
  const [noCache, setNoCache] = useState(false);
  const [errorModalStatus, setErrorModalStatus] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [errorPageTitle, setErrorPageTitle] = useState(null);
  const [errorPageMessage, setErrorPageMessage] = useState(null);
  const [historyStack, setHistoryStack] = useState([]);
  const [enableUpcomingStaysOption, setEnableUpcomingStaysOption] =
    useState(false);
  const [resetUpcomingStays, setResetUpcomingStays] = useState(false);
  const [upcomingStays, setUpcomingStays] = useState([]);
  const [upcomingStaysType, setUpcomingStaysType] = useState(null);
  const [upcomingStaysData, setUpcomingStaysData] = useState(null);
  const retryCount = useRef(0);

  const setRetryCount = (count = 0) => {
    retryCount.current = count;
  };

  const [upcomingStaysCheckInDate, setUpcomingStaysCheckInDate] =
    useState(null);

  const resetInitialize = useCallback(() => {
    setUserEmail(null);
    setBookingNumber(null);
    setLastName(null);
    setBooking(null);
    setGuests(null);
    setError(false);
    setErrorApi(false);
  }, []);

  const clearBooking = useCallback(() => {
    setBooking(null);
    Booking.setData(bookingNumber, null);
    Booking.setGuests(bookingNumber, null);
    Booking.setCard(bookingNumber, null);
  }, [bookingNumber]);

  const getUserEmail = useCallback(() => {
    let email = userEmail;
    if (isEmpty(email)) {
      let { search } = location;
      const indexOfSearch = location.pathname.indexOf("?");
      if (search === "" && indexOfSearch !== -1) {
        search = location.pathname.substring(indexOfSearch + 1);
      }
      const query = new URLSearchParams(search);
      const emailAddress = query.get("email");
      if (!isEmpty(emailAddress)) {
        email = emailAddress;
      }
    }
    if (!isEmpty(email)) {
      email = email.replace(/%20/g, "+").replace(/ /g, "+");
      email = email.replace(/null/g, "");
      setUserEmail(email);
    }
    return email;
  }, [location, userEmail]);

  const getProperty = useCallback(
    (newPropertyUid = null) => {
      return property || Property.getData(propertyUid || newPropertyUid);
    },
    [property, propertyUid]
  );

  const getGuestService = useCallback(() => {
    return (
      guestService || (propertyUid && Property.getGuestService(propertyUid))
    );
  }, [guestService, propertyUid]);

  const getPresets = useCallback(() => {
    return presets || Property.getPresetByUid(propertyUid);
  }, [presets, propertyUid]);

  const getConfiguration = useCallback(() => {
    return (
      configuration || (propertyUid && Property.getConfiguration(propertyUid))
    );
  }, [configuration, propertyUid]);

  const getBooking = useCallback(() => {
    return booking || (bookingNumber && Booking.getData(bookingNumber));
  }, [booking, bookingNumber]);

  const getGuests = useCallback(() => {
    return (guests && guests.numberOfGuests()) > 0
      ? guests
      : bookingNumber && new Guests(Booking.getGuests(bookingNumber));
  }, [guests, bookingNumber]);

  const setPropertyData = useCallback(
    (response) => {
      const propertyResponse = response.data;
      const isEvermore = ["Evermore Resort", "Evermore Resort Orlando", "Evermore"].includes(propertyResponse?.brand?.name)
      const propertyData = {
        isEvermore,
        ...propertyResponse,
      };
      setProperty(propertyData);
      Property.setData(propertyData);
    },
    [setProperty]
  );

  const setPresetData = useCallback(
    (response) => {
      const presetsResponse = response.data;
      const appDemoMode = get(presetsResponse, "app_feature_enable_demo_mode", {
        enable: false,
      });
      const primaryColor = get(property, "primary_color", "rgb(46, 30, 24)");
      const appBrandColorPrimary =
        presetsResponse.app_brand_color_primary || "#ffffff";
      const appBrandStylesheet = presetsResponse.app_brand_stylesheet;
      const appTopNavBgColor =
        presetsResponse.app_top_nav_bg_color || primaryColor;
      const appHomeTextTopNav =
        presetsResponse.app_home_text_top_nav || primaryColor;
      const titleColor =
        presetsResponse.app_home_list_item_title_color || primaryColor;
      const subtitleColor =
        presetsResponse.app_home_list_item_subtitle_color || primaryColor;
      const backgroundColorTopBar =
        presetsResponse.app_home_color_primary || primaryColor;
      const appHomeTextCallToAction =
        presetsResponse.app_home_text_call_to_action || "#ffffff";
      const appHomeTextDetailsPrecheckin =
        presetsResponse.app_home_text_details_precheckin || appHomeTextTopNav;
      const otaEmailList = get(presetsResponse, "pmsconfig_ota_email_list");
      const appWebstoreHideLanguageOption =
        get(presetsResponse, "app_webstore_hide_language_option") || false;
      const appAuthenticationType = get(
        presetsResponse,
        "app_authentication_type",
        "OKKAMI"
      ).toUpperCase();
      const textColorPrecheckin =
        get(presetsResponse, "app_home_text_details_precheckin") || "#32C33D";
      const appFeatureEnableSaveIdVerificationToProfile =
        get(
          presetsResponse,
          "app_feature_enable_save_id_verification_to_profile"
        ) || false;
      const fontSize = 20;
      const buttonStyle =
        get(presetsResponse, "app_brand_call_to_acton_button_style") || "none";
      const appHomeCopyrightTermsText =
        get(presetsResponse, "app_home_copyright_terms_text") || "";
      const appHomeGlobalBackgroundColor =
        get(presetsResponse, "app_home_global_background_color") || "#ffffff";
      const appHomeRowDividerColor =
        get(presetsResponse, "app_home_row_divider_color") || "#D7D7D7";
      let buttonStyleName = "";
      switch (buttonStyle) {
        case "none":
        case "round":
          buttonStyleName = "rounded-button";
          break;
        case "square":
          buttonStyleName = "square-button";
          break;
        case "small corner radius":
          buttonStyleName = "square-rounded-button";
          break;
        default:
          break;
      }

      const amPm =
        get(presetsResponse, "standard_time_hour_format") === "12-hour";

      const presetsData = {
        ...presetsResponse,
        demoMode: appDemoMode.enable,
        primaryColor,
        appBrandColorPrimary,
        appBrandStylesheet,
        appTopNavBgColor,
        appHomeTextTopNav,
        titleColor,
        subtitleColor,
        backgroundColorTopBar,
        appHomeTextCallToAction,
        appHomeTextDetailsPrecheckin,
        otaEmailList,
        appAuthenticationType,
        fontSize,
        textColorPrecheckin,
        appFeatureEnableSaveIdVerificationToProfile,
        buttonStyleName,
        appHomeCopyrightTermsText,
        appHomeGlobalBackgroundColor,
        appHomeRowDividerColor,
        appWebstoreHideLanguageOption,
        amPm,
      };
      setPresets(presetsData);
      Property.setPreset(propertyUid, presetsData);

      let defaultLangData = Property.getDefaultLanguage() ?? {};
      const defaultLanguage =
        presetsResponse?.app_feature_enable_default_language
          ?.app_default_language;

      if (defaultLanguage) {
        if (!defaultLangData.changed) {
          document.documentElement.setAttribute("lang", defaultLanguage);
          setLanguage(defaultLanguage);
          i18n.changeLanguage(defaultLanguage);
        }
        Property.setDefaultLanguage({
          language: defaultLanguage,
          changed: false,
        });
      }
    },
    [setPresets, property, propertyUid, i18n]
  );

  const setGuestServiceData = useCallback(
    (response) => {
      const guestServiceResponse = response.data;
      const schema = JSON.parse(get(guestServiceResponse, "schema", "{}"));
      const enableSpecialRequest = get(
        schema,
        "data.additional_information.enable_special_request",
        false
      );
      const requiredSpecialRequest = get(
        schema,
        "data.additional_information.required_special_request",
        false
      );
      const enableSignature = get(
        schema,
        "data.guest_detail.personal_information.enable_signature",
        false
      );
      const requiredSignature = get(
        schema,
        "data.guest_detail.personal_information.required_signature",
        false
      );
      const enableDateOfBirth = get(
        schema,
        "data.guest_detail.personal_information.enable_date_of_birth",
        false
      );
      const requiredDateOfBirth = get(
        schema,
        "data.guest_detail.personal_information.required_date_of_birth",
        false
      );
      const enableComments = get(
        schema,
        "data.additional_information.enable_comments",
        false
      );
      const requiredComments = get(
        schema,
        "data.additional_information.required_comments",
        false
      );
      const enableArrivalInformation = get(
        schema,
        "data.additional_information.enable_arrival_information",
        false
      );
      const flightArrivalTimeInterval = get(
        schema,
        "data.additional_information.flight_arrival_time_interval",
        false
      );
      const arrivalTimeInterval = get(
        schema,
        "data.additional_information.arrival_time_interval",
        false
      );
      const requiredArrivalInformation = get(
        schema,
        "data.additional_information.required_arrival_information",
        false
      );
      const enableArrivalTime = get(
        schema,
        "data.additional_information.enable_arrival_time",
        false
      );
      const requiredArrivalTime = get(
        schema,
        "data.additional_information.required_arrival_time",
        false
      );
      const enablePassport = get(
        schema,
        "data.guest_detail.document_collection.enable_passport_collection",
        true
      );
      const enableTM6 = get(
        schema,
        "data.guest_detail.document_collection.enable_tm_6_collection",
        true
      );
      const enablePassportExpiration = get(
        schema,
        "data.guest_detail.document_collection.enable_passport_expiration_date_collection",
        true
      );
      const additionalInformationDescription = get(
        schema,
        "data.additional_information.description"
      );
      const privacyStatementLink = get(
        schema,
        "data.guest_detail.personal_information.terms_links.privacy_statement_link",
        "https://www.okkami.com/privacy-policy"
      );
      const privacyPolicyLink = get(
        schema,
        "data.guest_detail.personal_information.terms_links.privacy_policy_link",
        "https://www.okkami.com/privacy-policy"
      );
      const termsAndConditionsLink = get(
        schema,
        "data.guest_detail.personal_information.terms_links.terms_and_conditions_link",
        "https://www.okkami.com/terms-and-conditions"
      );
      const phoneRequired = get(
        schema,
        "data.guest_detail.personal_information.phone_number_required",
        false
      );
      const enableNonLatinChar = get(
        schema,
        "data.guest_detail.personal_information.enable_non_latin_char",
        false
      );
      const allowRequestNumberOfKeys = get(
        schema,
        "data.additional_information.allows_request_number_of_keys.enable",
        false
      );
      const allGuestDetailRequired =
        get(schema, "data.guest_detail.guest_detail_required", false) || false;

      const titleRequestNumberOfKeys = get(
        schema,
        "data.additional_information.allows_request_number_of_keys.title",
        ""
      );
      const howToPickUpYourKey = get(
        schema,
        "data.additional_information.allows_request_number_of_keys.how_to_pick_up_your_key",
        ""
      );
      const numberOfKeys = get(
        schema,
        "data.additional_information.allows_request_number_of_keys.number",
        1
      );
      const depositInformationTitle = get(
        schema,
        "data.deposit_information.title",
        "DEPOSIT INFO"
      );
      const depositInformation = get(
        schema,
        "data.deposit_information.description",
        ""
      );
      const skipPayment = get(
        schema,
        "data.deposit_information.allow_to_skip",
        false
      );
      const documentCollectionDescription = get(
        schema,
        "data.guest_detail.document_collection.description"
      );
      const enableDisplayOfCheckInTime = get(
        schema,
        "data.confirmation.pre_checkin_confirm_message.enable_display_of_checkin_time",
        false
      );
      const confirmMessageDescription = get(
        schema,
        "data.confirmation.pre_checkin_confirm_message.description",
        `${t("WE_ARE_LOOKING_FORWARD")} ${t("WE_WILL_NOTIFY_YOU")}`
      );
      const callToActionText = get(
        schema,
        "data.confirmation.pre_checkin_confirm_message.call_to_action_text"
      );
      const storeUrl = get(
        schema,
        "data.confirmation.pre_checkin_confirm_message.store_url"
      );
      const enableLicensePlateCollection = get(
        schema,
        "data.guest_detail.personal_information.enable_license_plate_collection",
        false
      );
      const enableAdditional =
        enableSpecialRequest ||
        enableComments ||
        enableArrivalInformation ||
        enableArrivalTime;

      const loadingImage =
        get(guestServiceResponse, "loading_image") &&
        storeFunction.resizeImage(
          get(guestServiceResponse, "loading_image"),
          1024,
          1024
        );
      const errorImage =
        get(guestServiceResponse, "error_image") &&
        storeFunction.resizeImage(
          get(guestServiceResponse, "error_image"),
          1024,
          1024
        );
      const disableIdVerification = get(
        schema,
        "data.guest_detail.basic_information.disable_id_verification",
        false
      );
      const enableGuestListManagement = get(
        schema,
        "data.guest_detail.basic_information.enable_guest_list_management",
        false
      );
      const preventPrimaryGuestEmailEditing = get(
        schema,
        "data.guest_detail.basic_information.prevent_primary_guest_email_editing",
        false
      );
      const inputCallToAction = get(
        schema,
        "data.booking_info.booking_search_fallback.input_call_to_action"
      );
      const enableLastName = get(
        schema,
        "data.booking_info.booking_search_fallback.enable_last_name"
      );
      const upcomingStayTitle = get(
        schema,
        "data.booking_info.upcoming_stay.title"
      );
      const upcomingStayDescription = get(
        schema,
        "data.booking_info.upcoming_stay.description"
      );
      const enableAutomaticLookup = get(
        schema,
        "data.booking_info.upcoming_stay.enable_automatic_lookup"
      );
      const bookingInfoImage = get(guestServiceResponse, "booking_info_image");
      const errorTitle = get(schema, "data.alert_screen.error.title");
      const errorDescription = get(
        schema,
        "data.alert_screen.error.description"
      );
      const loadingTitle = get(
        schema,
        "data.alert_screen.booking_retrieval.title"
      );
      const loadingDescription = get(
        schema,
        "data.alert_screen.booking_retrieval.description"
      );
      const processingBookingTitle =
        (schema, "data.alert_screen.processing_booking.title");
      const processingBookingDescription =
        (schema, "data.alert_screen.processing_booking.description");
      const bookingSearchFallbackTitle = get(
        schema,
        "data.booking_info.booking_search_fallback.title"
      );
      const bookingSearchFallbackDescription = get(
        schema,
        "data.booking_info.booking_search_fallback.description"
      );
      const bookingSearchFallbackEnableArrivalDate = get(
        schema,
        "data.booking_info.booking_search_fallback.enable_arrival_date"
      );
      const preventPrimaryGuestEditing = get(
        schema,
        "data.guest_detail.basic_information.prevent_primary_guest_editing",
        false
      );
      const allowWithoutEmail = get(
        schema,
        "data.guest_detail.basic_information.allow_without_email",
        false
      );
      const personalInformationDescription = get(
        schema,
        "data.guest_detail.personal_information.description"
      );
      const basicInformationDescription = get(
        schema,
        "data.guest_detail.basic_information.description"
      );
      const skipAllPage =
        !enablePassport && !enableTM6 && !enablePassportExpiration;
      const guestsTerms = get(
        schema,
        "data.guest_detail.personal_information.guests_terms",
        []
      );
      const enableAddressCollection = get(
        schema,
        "data.guest_detail.personal_information.enable_address_collection",
        false
      );
      const requiredAddressCollection = get(
        schema,
        "data.guest_detail.personal_information.required_address_collection",
        false
      );
      const enablePreferenceCollection = get(
        schema,
        "data.guest_detail.personal_information.enable_preference_collection",
        false
      );
      const data = {
        ...guestServiceResponse,
        enableSignature,
        requiredSignature,
        enableDateOfBirth,
        requiredDateOfBirth,
        disableIdVerification,
        enableGuestListManagement,
        preventPrimaryGuestEmailEditing,
        schema,
        enableSpecialRequest,
        requiredSpecialRequest,
        enableComments,
        requiredComments,
        enableArrivalInformation,
        requiredArrivalInformation,
        enableArrivalTime,
        requiredArrivalTime,
        enablePassport,
        enableTM6,
        enablePassportExpiration,
        enableAdditional,
        loadingImage,
        errorImage,
        inputCallToAction,
        enableLastName,
        bookingInfoImage,
        upcomingStayTitle,
        upcomingStayDescription,
        errorTitle,
        errorDescription,
        loadingTitle,
        loadingDescription,
        processingBookingTitle,
        processingBookingDescription,
        bookingSearchFallbackTitle,
        bookingSearchFallbackDescription,
        bookingSearchFallbackEnableArrivalDate,
        allGuestDetailRequired,
        allowRequestNumberOfKeys,
        titleRequestNumberOfKeys,
        howToPickUpYourKey,
        numberOfKeys,
        depositInformation,
        depositInformationTitle,
        skipPayment,
        privacyStatementLink,
        privacyPolicyLink,
        termsAndConditionsLink,
        phoneRequired,
        enableNonLatinChar,
        preventPrimaryGuestEditing,
        allowWithoutEmail,
        skipAllPage,
        basicInformationDescription,
        personalInformationDescription,
        documentCollectionDescription,
        guestsTerms,
        enableDisplayOfCheckInTime,
        confirmMessageDescription,
        additionalInformationDescription,
        enableLicensePlateCollection,
        enableAutomaticLookup,
        enableAddressCollection,
        requiredAddressCollection,
        callToActionText,
        storeUrl,
        enablePreferenceCollection,
        flightArrivalTimeInterval,
        arrivalTimeInterval
      };
      setGuestService(data);
      Property.setGuestService(propertyUid, data);
    },
    [setGuestService, propertyUid, t]
  );

  const setConfigurationData = useCallback(
    (response) => {
      const configurationResponse = response.data;
      const pmsInterfaceType = get(configurationResponse, "pms_interface_type");
      const enableBackBtn = ["opera_minor_kiosk", "infor", "hotelkey"].includes(
        pmsInterfaceType
      );
      const checkPreCheckInInterfaceTypeIsOpera = [
        "opera_kiosk",
        "opera_minor_kiosk",
        "opera_ihg_kiosk",
      ].includes(pmsInterfaceType);
      const checkPreCheckInInterfaceTypeIsHotelKey =
        pmsInterfaceType === "hotelkey";
      const checkInTimeText =
        get(configurationResponse, "checkin_time", "--:--") || "--:--";
      const checkOutTimeText =
        get(configurationResponse, "checkout_time", "--:--") || "--:--";
      const paymentType = get(configurationResponse, "payment_type");
      const preCheckInWindow = get(configurationResponse, "pre_checkin_window");
      const notAllowedPreCheckInAfterCheckTime = get(
        configurationResponse,
        "not_allowed_pre_chki_after_chki_time"
      );
      const optInTermsTitle = get(
        configurationResponse,
        "opt_in_terms_title",
        t("OPTIONAL_TERMS")
      );
      const data = {
        ...configurationResponse,
        paymentType,
        pmsInterfaceType,
        enableBackBtn,
        checkInTimeText,
        checkOutTimeText,
        optInTermsTitle,
        preCheckInWindow,
        notAllowedPreCheckInAfterCheckTime,
        checkPreCheckInInterfaceTypeIsOpera,
        checkPreCheckInInterfaceTypeIsHotelKey,
      };
      setConfiguration(data);
      Property.setConfiguration(propertyUid, data);
    },
    [setConfiguration, propertyUid, t]
  );

  const setAccessTokenData = useCallback(async () => {
    await TokenService.getProperty(propertyUid)
      .then((response) => {
        const newAccessToken = get(response.data, "access_token");
        if (newAccessToken) {
          setAccessToken(newAccessToken);
          Booking.setUserToken(propertyUid, newAccessToken);
        }
        return newAccessToken;
      })
      .catch((error) => {
        console.error(error);
        setErrorApi(true);
      });
  }, [propertyUid]);

  const getAccessToken = useCallback(async () => {
    let newAccessToken =
      accessToken || (propertyUid && Booking.getUserToken(propertyUid));
    if (isEmpty(newAccessToken)) {
      newAccessToken = await setAccessTokenData();
    } else setAccessToken(newAccessToken);
    return newAccessToken;
  }, [accessToken, propertyUid, setAccessTokenData]);

  const updateGuests = useCallback(
    (newGuests) => {
      setGuests(newGuests);
      Booking.setGuests(bookingNumber, newGuests.allGuests());
    },
    [bookingNumber]
  );

  const isMatchtedGuestEmail = useCallback(
    (guest) => {
      return get(guest, "email") === getUserEmail();
    },
    [getUserEmail]
  );

  // Method to show up error page
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateErrorPage = (
    isError = true,
    errorTitle = null,
    errorMessage = null
  ) => {
    setError(isError);
    setErrorPageTitle(errorTitle);
    setErrorPageMessage(errorMessage);
  };

  const getPhoneCode = (phoneCode, countries) => {
    if (!isNaN(parseInt(phoneCode)) && phoneCode !== "0") return phoneCode

    return countries[phoneCode] ? countries[phoneCode].phone : phoneCode
  }

  const storeBookingData = useCallback(
    (bookingNumber, data) => {
      const additionalGuestsInfo = get(data, "additionalGuestData", []);
      let totalOfGuests = guestService.enableGuestListManagement
        ? additionalGuestsInfo.length + 1
        : get(data, "total_guests", 1);
      if (totalOfGuests > 0) {
        let guestsInstance = new Guests();
        let countryCode =
          get(data, "address.country_of_residence_code") ??
          get(
            presets,
            "app_feature_enable_default_language.app_default_language"
          );
        let country = "";
        let phoneCode =
          get(
            presets,
            "app_feature_enable_default_language.app_phone_country_code"
          ) ?? "0";
        if (countries[countryCode]) {
          country = countries[countryCode].name;
          phoneCode = getPhoneCode(phoneCode, countries);
        }

        const isCurrentGuestMain =
          isMatchtedGuestEmail(data) ||
          (!guestService.enableGuestListManagement && isEmpty(getUserEmail()));

        if (!isCurrentGuestMain)
          totalOfGuests = additionalGuestsInfo.length + 1;
        const preferences = get(data, "preferences") ?? [];
        const defaultPreferences = preferences
          ? preferences.map((preference) => ({
              ...preference,
              selected: false,
            }))
          : [];
        let phoneCountryCode = getPhoneCode(get(data, "phone.phone_country_code"), countries);
        guestsInstance.addEmptyByNumber(totalOfGuests, {
          preferences: defaultPreferences,
          country: country,
          countryCode: countryCode,
          nationality: get(data, "nationality"),
          phoneCode: phoneCountryCode || phoneCode,
        });
        let guest = {
          profileId: get(data, "profile_id"),
          title: get(data, "title"),
          firstName: get(data, "first_name"),
          lastName: get(data, "last_name"),
          email: get(data, "email"),
          country: country,
          countryCode: countryCode,
          nationality: get(data, "nationality"),
          phone: get(data, "phone.phone_number_without_code"),
          phoneCode: phoneCountryCode || phoneCode,
          mainGuest: true,
          currentGuest: isCurrentGuestMain,
          editable: isCurrentGuestMain,
          accountExist: get(data, "account_exist", false),
          guestId: get(data, "guest_id"),
          id: get(data, "id"),
          avatar: get(data, "avatar"),
          children: get(data, "children", []),
          licensePlate: get(data, "license_plate"),
          preCheckedInCompleted: get(data, "pre_checked_in_completed", false),
          ageGroup: get(data, "age_group"),
          preferences,
        };

        guestsInstance.UpdateByNumber(0, guest);
        let index = 1;
        let additionalPreferences;
        additionalGuestsInfo.forEach(function (item) {
          country = "";
          additionalPreferences =
            (get(item, "preferences") || []).length > 0
              ? get(item, "preferences")
              : defaultPreferences;
          let phoneCountryCode = getPhoneCode(get(data, "phone.phone_country_code"), countries);
          guest = {
            profileId: get(item, "profile_id"),
            title: get(item, "title"),
            firstName: get(item, "first_name"),
            lastName: get(item, "last_name"),
            email: get(item, "email"),
            country,
            countryCode,
            nationality: get(item, "nationality"),
            phone: get(data, "phone.phone_number_without_code"),
            phoneCode: phoneCountryCode || phoneCode,
            currentGuest: isMatchtedGuestEmail(item),
            editable: isMatchtedGuestEmail(item) || isCurrentGuestMain,
            accountExist: get(item, "account_exist", false),
            guestId: get(item, "guest_id"),
            id: get(item, "id"),
            avatar: get(item, "avatar"),
            children: get(item, "children", []),
            licensePlate: get(item, "license_plate"),
            preCheckedInCompleted: get(item, "pre_checked_in_completed", false),
            ageGroup: get(item, "age_group"),
            preferences: additionalPreferences,
          };
          guestsInstance.UpdateByNumber(index, guest);
          index++;
        });

        Booking.setData(bookingNumber, data);
        updateGuests(guestsInstance);
        const disallowedPreCheckIn =
          get(data, "status") === "disallowed_pre_checkin";
        if (disallowedPreCheckIn) {
          updateErrorPage(true, null, t("PRECHECK_BOOKING_WINDOW_ALERT"));
        }
      }
    },
    [
      getUserEmail,
      isMatchtedGuestEmail,
      guestService,
      updateGuests,
      updateErrorPage,
      presets,
      t,
    ]
  );

  const checkTotalCurrentGuest = (bookingResponse) => {
    const additionalGuestData = bookingResponse?.additional_guests_info || [];
    const totalAdditionalGuest = additionalGuestData.length;
    let totalChildOfAdditionalGuest = 0;
    for (const additionalGuestItem of additionalGuestData) {
      const childrenData = additionalGuestItem && additionalGuestItem.children;
      if (childrenData) {
        totalChildOfAdditionalGuest += childrenData.length;
      }
    }

    let totalChildOfPrimaryGuest = get(bookingResponse, "children", []).length;
    return (
      totalAdditionalGuest +
      totalChildOfAdditionalGuest +
      totalChildOfPrimaryGuest +
      1
    );
  };

  const totalInvitationsSent = (bookingResponse) => {
    const additionalGuestData = bookingResponse?.additional_guests_info || [];
    let totalInvitation = 0;
    for (const additionalGuestItem of additionalGuestData) {
      if (
        additionalGuestItem &&
        !additionalGuestItem.account_exist &&
        additionalGuestItem.email
      ) {
        totalInvitation += 1;
      }
    }

    return totalInvitation;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mockingUp = async () => {
    setLoading(true);
    await delay(5000);
    let dummy;
    if (get(configuration, "pmsInterfaceType") === "hotelkey") {
      dummy = HotelKeyDummy();
    } else if (enableUpcomingStaysOption) {
      callbackAfterFetchingUpcoming(UpcomingDummy());
    } else {
      dummy = Dummy();
    }
    if (dummy) callbackAfterFetching(dummy);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const callbackAfterFetching = (response) => {
    if (get(response.data, "status") === "fetching") {
      setTimeout(() => {
        getBookingDataApi();
      }, 3000); // set timer to call the booking api
    } else {
      setRetryCount(0);
      let bookingResponse = get(response.data, "booking");
      const checkIn = get(bookingResponse, "check_in");
      const checkInDate = moment.parseZone(checkIn);
      const checkInFormatDate = checkInDate.format("YYYY-MM-DD");
      const checkInDateText = checkInDate.format("DD MMM YYYY");
      const checkOut = get(bookingResponse, "check_out");
      const checkOutDate = moment.parseZone(checkOut);
      const checkOutFormatDate = checkOutDate.format("YYYY-MM-DD");
      const checkOutDateText = checkOutDate.format("DD MMM YYYY");
      const totalGuests = get(bookingResponse, "total_guests", 0);
      const totalCurrentGuest = checkTotalCurrentGuest(bookingResponse);
      const totalInviteSent = totalInvitationsSent(bookingResponse);
      const guestReachLimit = totalCurrentGuest >= totalGuests;
      Property.setCurrencyCode(get(bookingResponse, "currency"));
      let mainGuestIsPrimaryGuest =
        get(bookingResponse, "email") === getUserEmail() &&
        guestService.enableGuestListManagement;
      const additionalGuestData = bookingResponse?.additional_guests_info || [];
      const atLeastGuestRequired = guestService.allGuestDetailRequired
        ? get(bookingResponse, "total_guests")
        : 1;

      const additionalGuestIsPrimary =
        additionalGuestData.filter((g) => get(g, "email") === getUserEmail())
          .length > 0;
      let additionalGuestIsPrimaryGuest = additionalGuestIsPrimary;
      const amountDescription = get(
        bookingResponse,
        "pre_auth_amount.total_pre_auth_in_text"
      );
      const bookingId = get(bookingResponse, "booking_id");

      bookingResponse = {
        ...bookingResponse,
        bookingId,
        checkInDate,
        checkOutDate,
        checkInDateText,
        checkOutDateText,
        checkInFormatDate,
        checkOutFormatDate,
        totalGuests,
        totalCurrentGuest,
        guestReachLimit,
        mainGuestIsPrimaryGuest,
        additionalGuestData,
        additionalGuestIsPrimary,
        additionalGuestIsPrimaryGuest,
        atLeastGuestRequired,
        totalInviteSent,
        amountDescription,
      };

      if (
        [
          "waiting_for_pre_checkin",
          "booking_in_the_past",
          "pre_check_in_completed",
          "pre_checkin_completed",
          "room_is_ready",
        ].includes(get(response.data, "booking.status"))
      ) {
        const data = {
          ...bookingResponse,
          isBookingActive: false,
        };
        setBooking(data);
        storeBookingData(bookingNumber, data);
        if (!keepLoading) setLoading(false);
      } else if (["finish", "ok"].includes(get(response.data, "status"))) {
        const data = {
          ...bookingResponse,
          isBookingActive: true,
        };
        setBooking(data);
        storeBookingData(bookingNumber, data);
        if (!keepLoading) setLoading(false);
      } else {
        setError(true);
        setLoading(false);
      }
    }
  };

  const getBookingDataApi = useCallback(async () => {
    const newAccessToken = await getAccessToken();
    if (MAX_RETRY_COUNT > retryCount.current) {
      setRetryCount(retryCount.current + 1);
    } else {
      setLoading(false);
      setErrorApi(true);
      setRetryCount(0);
      return;
    }
    await BookingService.getHotelKeyBooking({
      accessToken: newAccessToken,
      bookingNumber,
      lastName,
      language,
      checkInDate: arrivalDate
        ? encodeURIComponent(decodeURIComponent(arrivalDate).replace(/ /g, "+"))
        : null,
    })
      .then((response) => {
        callbackAfterFetching(response);
      })
      .catch((error) => {
        if (get(error.response, "status") === 401) {
          setAccessTokenData();
        }
        console.error(error);
        setLoading(false);
        setErrorApi(true);
        setRetryCount(0);
      });
  }, [
    bookingNumber,
    lastName,
    language,
    getAccessToken,
    setAccessTokenData,
    callbackAfterFetching,
    arrivalDate,
    retryCount,
  ]);

  const getBookingData = useCallback(async () => {
    setErrorApi(false);
    const bookingCached = getBooking();
    await getAccessToken();
    if (!resetBooking && bookingCached) {
      setBooking(bookingCached);
      setGuests(getGuests());
      if (!keepLoading) setLoading(false);
    } else if (resetBooking) {
      clearBooking();
      setResetBooking(false);
      if (presets.demoMode) {
        mockingUp();
      } else if (bookingNumber && language) {
        getBookingDataApi();
      }
    }
  }, [
    bookingNumber,
    language,
    getAccessToken,
    resetBooking,
    getBooking,
    getGuests,
    presets,
    mockingUp,
    getBookingDataApi,
    clearBooking,
    keepLoading,
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const callbackAfterFetchingUpcoming = (response) => {
    if (get(response.data, "status") === "fetching") {
      setTimeout(() => {
        getUpcomingStaysDataApi();
      }, 5000); // set timer to call the booking api
    } else {
      setRetryCount(0);
      let upcomingStaysResponse = get(response.data, "upcoming_stays");
      const upcomingStaysList = upcomingStaysResponse.map((bookingResponse) => {
        const checkIn = get(bookingResponse, "check_in");
        const checkInDate = moment(checkIn);
        const checkInFormatDate = checkInDate.format("YYYY-MM-DD");
        const checkInDateText = checkInDate.format("DD MMM YYYY");
        const checkOut = get(bookingResponse, "check_out");
        const checkOutDate = moment(checkOut);
        const checkOutFormatDate = checkOutDate.format("YYYY-MM-DD");
        const checkOutDateText = checkOutDate.format("DD MMM YYYY");
        const totalGuests = get(bookingResponse, "total_guests", 0);
        const totalCurrentGuest = checkTotalCurrentGuest(bookingResponse);
        const totalInviteSent = totalInvitationsSent(bookingResponse);
        const guestReachLimit = totalCurrentGuest >= totalGuests;
        Property.setCurrencyCode(get(bookingResponse, "currency"));
        const additionalGuestData =
          bookingResponse?.additional_guests_info || [];
        const atLeastGuestRequired = guestService.allGuestDetailRequired
          ? get(bookingResponse, "total_guests")
          : 1;

        const amountDescription = get(
          bookingResponse,
          "pre_auth_amount.total_pre_auth_in_text"
        );
        const bookingId = get(bookingResponse, "booking_id");

        let isBookingActive = true;
        if (
          [
            "waiting_for_pre_checkin",
            "booking_in_the_past",
            "pre_check_in_completed",
            "pre_checkin_completed",
            "room_is_ready",
          ].includes(get(bookingResponse, "status"))
        ) {
          isBookingActive = false;
        }

        return {
          ...bookingResponse,
          isBookingActive,
          bookingId,
          checkInDate,
          checkOutDate,
          checkInDateText,
          checkOutDateText,
          checkInFormatDate,
          checkOutFormatDate,
          totalGuests,
          totalCurrentGuest,
          guestReachLimit,
          additionalGuestData,
          atLeastGuestRequired,
          totalInviteSent,
          amountDescription,
          mainGuestIsPrimaryGuest: true,
          additionalGuestIsPrimary: false,
          additionalGuestIsPrimaryGuest: false,
        };
      });

      if (["finish", "ok"].includes(get(response.data, "status"))) {
        setUpcomingStays(upcomingStaysList || []);
        if (!keepLoading) setLoading(false);
        if ((upcomingStaysList || []).length === 0) setError(true);
      } else {
        setError(true);
        setLoading(false);
      }
    }
  };

  const getUpcomingStaysDataApi = useCallback(async () => {
    const newAccessToken = await getAccessToken();
    if (MAX_RETRY_COUNT > retryCount.current) {
      setRetryCount(retryCount.current + 1);
    } else {
      setLoading(false);
      setErrorApi(true);
      setRetryCount(0);
      return;
    }
    await BookingService.getUpcomingBooking(
      newAccessToken,
      upcomingStaysCheckInDate,
      upcomingStaysType,
      encodeURIComponent(
        decodeURIComponent(upcomingStaysData).replace(/ /g, "+")
      ),
      language
    )
      .then((response) => {
        callbackAfterFetchingUpcoming(response);
      })
      .catch((error) => {
        if (get(error.response, "status") === 401) {
          setAccessTokenData();
        }
        console.error(error);
        setLoading(false);
        setErrorApi(true);
        setRetryCount(0);
      });
  }, [
    language,
    setAccessTokenData,
    callbackAfterFetchingUpcoming,
    upcomingStaysCheckInDate,
    upcomingStaysData,
    upcomingStaysType,
    getAccessToken,
    retryCount,
  ]);

  const getUpcomingStaysData = useCallback(async () => {
    setErrorApi(false);
    setLoading(true);
    await getAccessToken();
    if (resetUpcomingStays || isEmpty(upcomingStays)) {
      setResetUpcomingStays(false);
      if (presets.demoMode) {
        mockingUp();
      } else if (upcomingStaysData && upcomingStaysType && language) {
        getUpcomingStaysDataApi();
      }
    }
  }, [
    language,
    resetUpcomingStays,
    presets,
    mockingUp,
    getUpcomingStaysDataApi,
    upcomingStays,
    upcomingStaysData,
    upcomingStaysType,
    getAccessToken,
  ]);

  const delay = (delay) => {
    return new Promise((res) => setTimeout(res, delay));
  };

  const getAllData = useCallback(async () => {
    const propertyCached = getProperty();
    const presetsCached = getPresets();
    const guestServiceCached = getGuestService();
    const configurationCached = getConfiguration();
    const userEmailCached = Property.getEmail(propertyUid);
    if (isEmpty(userEmail) && userEmailCached) setUserEmail(userEmailCached);
    if (
      !noCache &&
      propertyCached &&
      guestServiceCached &&
      presetsCached &&
      configurationCached
    ) {
      if (isEmpty(property)) setProperty(propertyCached);
      if (isEmpty(guestService)) setGuestService(guestServiceCached);
      if (isEmpty(presets)) setPresets(presetsCached);
      if (isEmpty(configuration)) setConfiguration(configurationCached);
      if (!resetBooking && !enableUpcomingStaysOption && !keepLoading)
        setLoading(false);
    } else if (propertyUid && language) {
      setLoading(true);
      setErrorApi(false);
      await Promise.all([
        PropertiesService.getProperty(propertyUid, language),
        PresetsService.getPreset(propertyUid),
        GuestServicesService.getGuestService(
          propertyUid,
          language,
          "pre_checkin"
        ),
        ConfigurationsService.getConfiguration(propertyUid, language),
      ])
        .then((response) => {
          const [
            propertyResponse,
            presetResponse,
            guestServiceResponse,
            configurationResponse,
          ] = response;

          if (!isMounted) return;

          setPropertyData(propertyResponse);
          setPresetData(presetResponse);
          setGuestServiceData(guestServiceResponse);
          setConfigurationData(configurationResponse);
          setNoCache(false);
        })
        .catch((error) => {
          if (get(error.response, "status") === 401) {
            setAccessTokenData();
          }
          console.error(error);
          setErrorApi(true);
        })
        .finally(
          () =>
            !resetBooking &&
            !enableUpcomingStaysOption &&
            !keepLoading &&
            setLoading(false)
        );
    }
  }, [
    userEmail,
    setNoCache,
    getProperty,
    getGuestService,
    getPresets,
    getConfiguration,
    setProperty,
    setGuestService,
    setPresets,
    setConfiguration,
    propertyUid,
    language,
    noCache,
    setPropertyData,
    setPresetData,
    setGuestServiceData,
    setConfigurationData,
    setLoading,
    setErrorApi,
    resetBooking,
    enableUpcomingStaysOption,
    setAccessTokenData,
    configuration,
    guestService,
    presets,
    property,
    keepLoading,
    isMounted,
  ]);

  const useFormInput = (initialState) => {
    const [formValues, setFormValues] = useState(initialState);

    const handleChange = useCallback(
      (event) => {
        const { name, value } = event.target;
        if (name.startsWith("children") && typeof value !== "object") {
          const [, childIndex, childField] = name.match(
            /^children\[(\d+)\]\.(\w+)$/
          );
          const updatedChildren = [...formValues.children];
          updatedChildren[childIndex] = {
            ...updatedChildren[childIndex],
            [childField]: value,
          };

          setFormValues((prevState) => ({
            ...prevState,
            children: updatedChildren,
          }));
        } else {
          setFormValues((prevState) => ({
            ...prevState,
            [name]: value,
          }));
        }
      },
      [formValues]
    );

    return [formValues, handleChange, setFormValues];
  };

  const convertAsciiUrl = (url) => {
    return {
      ...url,
      pathname: url.pathname.replace(/%3F/g, "?").replace(/%3D/g, "="),
    };
  };

  const fixSearchUrl = (url) => {
    let tmpUrl = url;
    let splitPath = tmpUrl.pathname.split("?");
    let pathname = splitPath[0];
    let search = splitPath[1] ? `?${splitPath[1]}` : tmpUrl.search;
    return {
      ...tmpUrl,
      pathname,
      search,
    };
  };

  // Method to save the URL in history stack
  const saveUrlToHistory = (url) => {
    setHistoryStack((prevStack) => {
      let tmpUrl = convertAsciiUrl(url);
      tmpUrl = fixSearchUrl(url);

      removeOldHistories(tmpUrl);
      const histories = [tmpUrl, ...Property.getHistoryStack()];
      Property.setHistoryStack(histories);
      return histories;
    });
  };

  // Method to pop the URL from history stack
  const popUrlFromHistory = () => {
    const newStack = [...historyStack];
    newStack.splice(0, 1);
    setHistoryStack(newStack);
    Property.setHistoryStack(newStack);
    return newStack.length > 1 && newStack[0];
  };

  // Method to handle new URL and maintain unique URLs in history
  const handleNewUrl = (newLocation, readFromCache = false) => {
    let url = newLocation;
    url = convertAsciiUrl(url);
    url = fixSearchUrl(url);

    removeOldHistories(url, readFromCache);
    saveUrlToHistory(url);
  };

  const removeOldHistories = (url, readFromCache = true) => {
    let historyStackItem = [...historyStack];
    if (readFromCache && isEmpty(historyStackItem)) {
      historyStackItem = [...Property.getHistoryStack()];
      setHistoryStack(historyStackItem);
    }
    const urlPath = url.pathname;
    let index = historyStackItem.findIndex((item) => item.pathname === urlPath);
    while (index !== -1) {
      const newHistory = [...historyStackItem];
      newHistory.splice(0, index + 1);
      setHistoryStack(newHistory);
      Property.setHistoryStack(newHistory);
      historyStackItem = newHistory;
      index = historyStackItem.findIndex((item) => item.pathname === urlPath);
    }
  };

  const isHomePage = (url = null, newPropertyUid = null) => {
    let currentUrl = url ? url : historyStack[0];
    if (isEmpty(historyStack)) {
      const historyStackItem = [...Property.getHistoryStack()];
      currentUrl = historyStackItem[0];
    }
    const homeUrls = [
      `/pre-checkin/${propertyUid || newPropertyUid}/home`,
      `/pre-checkin/${propertyUid || newPropertyUid}/booking-code`,
      `/pre-checkin/${propertyUid || newPropertyUid}/guest-name`,
      `/pre-checkin/${propertyUid || newPropertyUid}/guest-email`,
    ];
    return !currentUrl || homeUrls.indexOf(currentUrl.pathname) > -1;
  };

  const goToBack = (propertyUid, language, history) => {
    const historyStackItem = [...Property.getHistoryStack()];
    setError(false);
    setErrorApi(false);
    let isWebAppRedirectUrlResult = isWebAppRedirectUrl(historyStackItem[1]);
    if (!isWebAppRedirectUrlResult) {
      let url = `/pre-checkin/${propertyUid}/home?language=${language}`;
      const locationHistory = popUrlFromHistory();
      if (locationHistory && locationHistory?.pathname)
        url = `${locationHistory.pathname}${locationHistory.search}`;
      history.push(url);
    }
  };

  const isWebAppRedirectUrl = (url) => {
    if (url && url.search && url.search.indexOf("webapp_redirect_url") !== -1) {
      const query = new URLSearchParams(url.search);
      const webappRedirectUrl = query.get("webapp_redirect_url");
      if (!isEmpty(webappRedirectUrl)) {
        window.location.href = webappRedirectUrl;
        return true;
      }
    }
    return false;
  };

  const PreCheckInContainer = ({ containerName, children }) => {
    const history = useHistory();

    // Call useGetBrandTranslation directly inside the component
    const brandName = property?.brand?.name;
    const translations = useGetBrandTranslation(brandName);

    useEffect(() => {
      if (brandName) {
        i18n.addResources(language, "translation", translations);
      }
    }, [brandName, translations]);

    useEffect(() => {
      let loadingComponent = document.getElementById("loading-component");
      if (loadingComponent) {
        loadingComponent.remove();
      }

      let errorComponent = document.getElementById("error-component");
      if (errorComponent) {
        errorComponent.remove();
      }

      if (error || errorApi) {
        const root = document.getElementById("root");
        errorComponent = document.createElement("div");
        errorComponent.id = "error-component";
        errorComponent.className = "w-full h-full absolute inset-0";
        ReactDOM.render(
          <ErrorPage
            backgroundImage={guestService?.errorImage}
            brandStyleSheet={presets?.appBrandStylesheet}
            buttonStyleName={presets?.buttonStyleName}
            backgroundColorTopBar={presets?.backgroundColorTopBar}
            appHomeTextCallToAction={guestService?.appHomeTextCallToAction}
            title={errorPageTitle || guestService?.errorTitle}
            description={errorPageMessage || guestService?.errorDescription}
            backText={t("BACK")}
            backLink={() => goToBack(propertyUid, language, history)}
          />,
          errorComponent
        );
        root.appendChild(errorComponent);
        return;
      }

      if (loading || isEmpty(presets) || isEmpty(guestService)) {
        const root = document.getElementById("root");
        loadingComponent = document.createElement("div");
        loadingComponent.id = "loading-component";
        loadingComponent.className = "w-full h-full absolute inset-0";
        ReactDOM.render(
          <LoadingPage
            backgroundImage={guestService?.loadingImage}
            backgroundColor={presets?.appBrandColorPrimary}
            brandStyleSheet={presets?.appBrandStylesheet}
            title={guestService?.loadingTitle}
            description={guestService?.loadingDescription}
          />,
          loadingComponent
        );
        root.appendChild(loadingComponent);
        return;
      }
    }, [history]);

    return (
      <React.Fragment>
        {!(
          loading ||
          isEmpty(presets) ||
          isEmpty(guestService) ||
          error ||
          errorApi
        ) ? (
          <React.Fragment>
            <div
              id={containerName}
              className="h-full flex flex-col w-full">
              <BrandStyleSheet brandStyleSheet={presets.appBrandStylesheet} />
              <div className="flex flex-col h-full desktop:overflow-y-auto">
                {children}
                <CopyRightAndPrivacyComponent
                  regexFormFontSize={"10px"}
                  regexFormColor={presets.appHomeCopyrightTermsText}
                  titleColor={presets.titleColor}
                  subtitleColor={presets.subtitleColor}
                  backgroundColorTopBar={presets.backgroundColorTopBar}
                  appHomeTextCallToAction={presets.appHomeTextCallToAction}
                  allTerms={[{ regexForm: [t("COPYWRITE_PRIVACY_POLICY")] }]}
                />
              </div>
            </div>
            {property &&
              !isEmpty(property.supported_languages) &&
              (!presets ||
                (presets && !presets.appWebstoreHideLanguageOption)) && (
                <ButtonLanguage
                  onSelected={() => {
                    document.documentElement.setAttribute(
                      "lang",
                      i18n.language
                    );
                    setLanguage(i18n.language);
                    setNoCache(true);
                    setIsMounted(true);
                    Property.setDefaultLanguage({
                      language: i18n.language,
                      changed: true,
                    });
                    const searchParams = new URLSearchParams(location.search);
                    searchParams.set("language", i18n.language);
                    const newSearch = `?${searchParams.toString()}`;
                    const path = `${location.pathname}${newSearch}`;
                    history.replace(path);
                  }}
                  currentPage="store"
                  showButton={true}
                />
              )}

            {errorModalStatus && (
              <ErrorModal
                stateModal={errorModalStatus}
                buttonStyleName={presets?.buttonStyleName}
                openCloseModal={(modalStatus) =>
                  setErrorModalStatus(modalStatus)
                }
                textTitle={"Oops!"}
                btnErrorText={"Ok"}
                childText={errorMessage}
              />
            )}
          </React.Fragment>
        ) : null}
      </React.Fragment>
    );
  };

  const PreCheckInHeader = ({ title, headerLink, children, onBack }) => {
    const history = useHistory();
    return (
      <React.Fragment>
        <section
          id="header"
          className="relative w-full">
          <div
            className="flex py-8 px-4 text-white text-left cursor-pointer"
            style={{ backgroundColor: presets?.appTopNavBgColor }}
            onClick={() =>
              headerLink &&
              (onBack ? onBack() : goToBack(propertyUid, language, history))
            }>
            {headerLink && (
              <>
                <ArrowBackIos
                  style={{
                    fontSize: 20,
                    color: presets?.appHomeTextTopNav,
                    width: 50,
                  }}
                />
              </>
            )}
            <span
              className="pl-4 type-title large capitalize"
              style={{ color: presets?.appHomeTextTopNav }}>
              {title}
            </span>
            {children}
          </div>
        </section>
      </React.Fragment>
    );
  };

  const initialPreCheckIn = (props) => {
    const {
      home,
      match,
      reset,
      noCache,
      location: newLocation,
      enableUpcomingStays: newEnableUpcomingStays,
    } = props;

    const {
      propertyUid: newPropertyUid,
      bookingNumber: newBookingNumber,
      lastName: newLastName,
      bookingType,
      bookingInput,
      checkInBookingDate,
    } = match.params;

    if (home) clearLocalStorage();

    let tmpUrl = convertAsciiUrl(newLocation);
    tmpUrl = fixSearchUrl(tmpUrl);

    let { search } = tmpUrl;
    const query = new URLSearchParams(search);

    setIsBookingMounted(true);
    if (isEmpty(newBookingNumber)) {
      resetInitialize();
    }

    if (isHomePage(null, newPropertyUid)) {
      setBooking(null);
      setUpcomingStays([]);
    }

    const isExistBooking = Booking.getData(newBookingNumber);
    if (
      newBookingNumber &&
      ((isEmpty(booking) && reset) || isEmpty(isExistBooking))
    ) {
      setLoading(true);
      resetBookingData();
    }

    setEnableUpcomingStaysOption(newEnableUpcomingStays);
    if (!isEmpty(bookingType)) setUpcomingStaysType(bookingType);
    if (!isEmpty(bookingInput)) setUpcomingStaysData(bookingInput);
    if (!isEmpty(checkInBookingDate))
      setUpcomingStaysCheckInDate(checkInBookingDate);
    if (newEnableUpcomingStays && isEmpty(upcomingStays)) {
      setLoading(true);
      resetUpcomingStaysData();
    }

    if (!noCache && isEmpty(historyStack)) {
      handleNewUrl(tmpUrl, true);
    } else {
      handleNewUrl(tmpUrl);
    }

    setNoCache(noCache || false);
    if (!userEmail) {
      Property.setEmail(newPropertyUid, getUserEmail());
    }
    let i18nextLng = localStorage.getItem("i18nextLng");
    i18nextLng = i18nextLng.indexOf("-")
      ? i18nextLng.split("-")[0]
      : i18nextLng;
    const defaultLanguage =
      presets?.app_feature_enable_default_language?.app_default_language;
    const languageQuery =
      query.get("language") || defaultLanguage || i18nextLng || "en";
    setLanguage(languageQuery);
    i18n.changeLanguage(languageQuery);
    document.documentElement.setAttribute("lang", languageQuery);
    const arrDate = query.get("arrival_date");
    if (arrDate) setArrivalDate(arrDate);
    if (newPropertyUid) setPropertyUid(newPropertyUid);
    if (newLastName) setLastName(newLastName);
    if (newBookingNumber) setBookingNumber(newBookingNumber);
    if (
      (noCache && !isMounted) ||
      isEmpty(Property.getData(newPropertyUid)) ||
      isEmpty(Property.getPresetByUid(newPropertyUid)) ||
      isEmpty(Property.getGuestService(newPropertyUid)) ||
      isEmpty(Property.getConfiguration(newPropertyUid))
    ) {
      setNoCache(true);
      setIsMounted(true);
    }
  };

  const updateErrorModal = (errorModalStatus, errorMessage) => {
    setErrorModalStatus(errorModalStatus);
    setErrorMessage(errorMessage);
  };

  useEffect(() => {
    if (isMounted && propertyUid) {
      setIsMounted(false);
      getAllData();
    }
  }, [isMounted, propertyUid, getAllData]);

  useEffect(() => {
    if (isBookingMounted && bookingNumber && guestService && configuration) {
      setIsBookingMounted(false);
      getBookingData();
      return () => false;
    }
  }, [
    isBookingMounted,
    bookingNumber,
    guestService,
    getBookingData,
    configuration,
  ]);

  useEffect(() => {
    if (
      isUpcomingStaysMounted &&
      enableUpcomingStaysOption &&
      guestService &&
      configuration
    ) {
      setIsUpcomingStaysMounted(false);
      getUpcomingStaysData();
      return () => false;
    }
  }, [
    isUpcomingStaysMounted,
    guestService,
    enableUpcomingStaysOption,
    getUpcomingStaysData,
    configuration,
  ]);

  const resetBookingData = () => {
    setResetBooking(true);
    setIsBookingMounted(true);
  };

  const resetUpcomingStaysData = () => {
    setResetUpcomingStays(true);
    setIsUpcomingStaysMounted(true);
  };

  const clearLocalStorage = () => {
    const localStorahgeKeys = Object.keys(localStorage);
    localStorahgeKeys.forEach((key) => {
      if (
        key.indexOf("okkami_webstore_") !== -1 &&
        key.indexOf("defaultLanguage") === -1
      ) {
        localStorage.removeItem(key);
      }
    });
  };

  const contextValue = {
    location,
    isMounted,
    resetBooking,
    bookingNumber,
    propertyUid,
    lastName,
    userEmail,
    language,
    loading,
    error,
    errorApi,
    data: {
      property,
      guestService,
      presets,
      configuration,
      accessToken,
      booking,
      guests,
      upcomingStays,
    },
    func: {
      useFormInput,
      initialPreCheckIn,
      updateGuests,
      updateErrorModal,
      updateErrorPage,
      setLoading,
      setKeepLoading,
      setAccessTokenData,
      resetBookingData,
      clearLocalStorage,
    },
    components: { PreCheckInHeader, PreCheckInContainer },
  };

  return (
    <PreCheckInContext.Provider value={contextValue}>
      {children}
    </PreCheckInContext.Provider>
  );
};

export default PreCheckInContextProvider;
